import React, { useState, useEffect } from 'react';
import { Loader2, X } from 'lucide-react';
import { CreateCarrierIntegrationDto } from '@/core/api/types';
import { Company, SUPPORTED_CARRIERS } from '../types';
import { useCarrierIntegration, useUpdateCarrierIntegration } from '@/core/api/queries/carriers';
import { useUIState } from '@/core/UIStateProvider';
import { useCompanies } from '@/core/api/queries';

interface EditCarrierIntegrationModalProps {
  carrierIntegrationId: string | null;
  showEditCarrierIntegration: boolean;
  setShowEditCarrierIntegration: (show: boolean) => void;
}

export const EditCarrierIntegrationModal: React.FC<EditCarrierIntegrationModalProps> = ({
  carrierIntegrationId,
  showEditCarrierIntegration,
  setShowEditCarrierIntegration,
}) => {
  const companies = useCompanies();
  const { data: carrierIntegration } = useCarrierIntegration(carrierIntegrationId || '');

  const { addNotification } = useUIState();
  const updateCarrierIntegration = useUpdateCarrierIntegration();

  const [editCarrierIntegrationData, setEditCarrierIntegrationData] = useState<CreateCarrierIntegrationDto>({
    carrierName: '',
    lspId: '',
    authConfig: {},
    apiEndpoint: '',
    isActive: true,
    pollingConfig: {},
  });

  const [authConfigJson, setAuthConfigJson] = useState<string>('');
  const [pollingConfigJson, setPollingConfigJson] = useState<string>('');

  useEffect(() => {
    if (carrierIntegration) {
      setEditCarrierIntegrationData({
        carrierName: carrierIntegration.carrierName || '',
        lspId: carrierIntegration.lspId || '',
        authConfig: carrierIntegration.authConfig || {},
        apiEndpoint: carrierIntegration.apiEndpoint || '',
        isActive: carrierIntegration.isActive ?? true,
        pollingConfig: carrierIntegration.pollingConfig || {},
      });
      setAuthConfigJson(JSON.stringify(carrierIntegration.authConfig || {}, null, 2));
      setPollingConfigJson(JSON.stringify(carrierIntegration.pollingConfig || {}, null, 2));
    }
  }, [carrierIntegration]);

  if (!showEditCarrierIntegration) return null;

  const sortedSupportedCarriers = SUPPORTED_CARRIERS.sort((a, b) => a.localeCompare(b));

  const companiesData = (companies.data as Company[]) || [];
  const lspCompaniesData = companiesData.filter((company) => company.role === 'LSP');

  const handleUpdateCarrierIntegration = async (): Promise<void> => {
    try {
      const parsedData = {
        ...editCarrierIntegrationData,
        lspId: editCarrierIntegrationData.lspId || undefined,
        authConfig: authConfigJson ? JSON.parse(authConfigJson) : {},
        pollingConfig: pollingConfigJson ? JSON.parse(pollingConfigJson) : {},
      };
      await updateCarrierIntegration.mutateAsync({ id: carrierIntegration.id, data: parsedData });
      addNotification('success', 'Carrier integration updated successfully!');
      setShowEditCarrierIntegration(false);
      setEditCarrierIntegrationData({
        carrierName: parsedData?.carrierName || '',
        lspId: parsedData.lspId || '',
        authConfig: parsedData.authConfig,
        apiEndpoint: parsedData?.apiEndpoint || '',
        isActive: parsedData?.isActive || true,
        pollingConfig: parsedData?.pollingConfig || {},
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update carrier integration';
      addNotification('error', errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-2 w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Edit Carrier Integration</h3>
          <button
            onClick={() => setShowEditCarrierIntegration(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Carrier Name *</label>
            <select
              value={editCarrierIntegrationData.carrierName}
              onChange={(e) =>
                setEditCarrierIntegrationData({ ...editCarrierIntegrationData, carrierName: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Select carrier</option>
              {sortedSupportedCarriers.map((carrier) => (
                <option key={carrier} value={carrier}>
                  {carrier}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">LSP Company</label>
            <select
              value={editCarrierIntegrationData.lspId}
              onChange={(e) => setEditCarrierIntegrationData({ ...editCarrierIntegrationData, lspId: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Select LSP company</option>
              {lspCompaniesData.map((company) => (
                <option key={company.id} value={company.id || ''}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">API Endpoint *</label>
            <input
              type="text"
              value={editCarrierIntegrationData.apiEndpoint}
              onChange={(e) =>
                setEditCarrierIntegrationData({ ...editCarrierIntegrationData, apiEndpoint: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="https://api.carrier.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Auth Config (JSON)</label>
            <textarea
              value={authConfigJson}
              onChange={(e) => {
                setAuthConfigJson(e.target.value);
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
              placeholder='{"apiKey": "your-key", "apiSecret": "your-secret"}'
              rows={4}
            />
            <p className="mt-1 text-xs text-gray-500">Enter authentication configuration as JSON</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Polling Config (JSON)</label>
            <textarea
              value={pollingConfigJson}
              onChange={(e) => {
                setPollingConfigJson(e.target.value);
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
              placeholder='{"intervalMinutes": 30, "enabled": true}'
              rows={4}
            />
            <p className="mt-1 text-xs text-gray-500">Enter polling configuration as JSON</p>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isActive"
              checked={editCarrierIntegrationData.isActive}
              onChange={(e) =>
                setEditCarrierIntegrationData({ ...editCarrierIntegrationData, isActive: e.target.checked })
              }
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
              Active
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 ">
          <button
            onClick={() => setShowEditCarrierIntegration(false)}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateCarrierIntegration}
            disabled={
              !editCarrierIntegrationData.carrierName ||
              !editCarrierIntegrationData.apiEndpoint ||
              updateCarrierIntegration.isPending
            }
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors flex items-center space-x-2"
          >
            {updateCarrierIntegration.isPending && <Loader2 size={16} className="animate-spin" />}
            <span>Update Integration</span>
          </button>
        </div>
      </div>
    </div>
  );
};
